/**
 * Command Item Component
 *
 * Tek bir komut satırı — landing entry kartlarının kompakt hali.
 */

import React from "react";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { SlashCommand } from "../types/command-types";

interface CommandItemProps {
  command: SlashCommand;
  onSelect: () => void;
  isSelected?: boolean;
  /** Dock / dar composer: ikon küçülür, açıklama gizlenir */
  compact?: boolean;
}

export const CommandItem = React.forwardRef<
  HTMLButtonElement,
  CommandItemProps
>(({ command, onSelect, isSelected = false, compact = false }, ref) => {
  const { t, i18n } = useTranslation();

  return (
    <button
      ref={ref}
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      className={cn(
        "group flex w-full items-center text-left",
        "transition-[background-color,border-color,box-shadow,transform] duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-module-accent/50",
        compact
          ? "gap-2 rounded-xl border px-2 py-1.5"
          : "gap-3 rounded-2xl border px-3 py-2.5",
        isSelected
          ? "border-module-accent/35 bg-module-accent-soft shadow-[0_14px_32px_-22px_var(--module-accent)]"
          : "border-transparent hover:border-module-accent/20 hover:bg-white/70 dark:hover:bg-white/[0.04]",
      )}
    >
      {command.icon && (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-xl transition-transform duration-300",
            compact ? "size-8" : "size-10",
            isSelected
              ? "scale-105 bg-module-accent text-module-accent-foreground"
              : "bg-module-accent-soft text-module-accent group-hover:scale-105",
          )}
        >
          {command.icon}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-text-primary dark:text-gray-100">
            {t(command.label)}
          </span>
          {command.shortcut && (
            <span className="inline-flex shrink-0 items-center rounded-md border border-module-accent/20 bg-module-accent-soft px-1.5 py-0.5 font-mono text-[10px] font-medium text-module-accent">
              {command.shortcut}
            </span>
          )}
        </div>
        {!compact && command.description && (
          <p className="truncate text-xs text-text-tertiary dark:text-gray-400">
            {typeof command.description === "string"
              ? i18n.exists(command.description)
                ? t(command.description)
                : command.description
              : String(command.description)}
          </p>
        )}
      </div>

      {(command.type === "parent" || command.type === "dynamic") && (
        <ChevronRight className="size-4 shrink-0 text-text-quaternary transition-transform duration-200 group-hover:translate-x-0.5 dark:text-gray-500" />
      )}
    </button>
  );
});

CommandItem.displayName = "CommandItem";
